
import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SystemHealthChecker from './SystemHealthChecker';
import SystemStatusChecker from './SystemStatusChecker';
import { checkAuthConfig } from '@/utils/auth-launch-check';

const AdminSupportTab = () => {
  const [activeSubTab, setActiveSubTab] = useState("health");

  return (
    <div className="space-y-6">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="space-y-4">
        <TabsList className="grid grid-cols-3 max-w-md">
          <TabsTrigger value="health">System Health</TabsTrigger>
          <TabsTrigger value="auth">Auth Check</TabsTrigger>
          <TabsTrigger value="logs">System Logs</TabsTrigger>
        </TabsList>
        
        <TabsContent value="health" className="space-y-6">
          <SystemHealthChecker />
          <SystemStatusChecker />
        </TabsContent>
        
        <TabsContent value="auth">
          <AuthSystemLaunchCheck />
        </TabsContent>
        
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>System Logs</CardTitle>
              <CardDescription>
                Die letzten Systemereignisse und Fehler
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px] overflow-y-auto bg-gray-900 text-gray-200 p-4 rounded font-mono text-sm">
                {/* This would be populated from a logs endpoint */}
                <div className="opacity-50">[2023-05-16 12:34:56] [INFO] System started</div>
                <div className="opacity-50">[2023-05-16 12:35:01] [INFO] Auth service initialized</div>
                <div className="opacity-50">[2023-05-16 12:35:03] [INFO] Database connected</div>
                <div className="text-yellow-300">[2023-05-16 12:35:10] [WARNING] Email service slow response (350ms)</div>
                <div className="opacity-50">[2023-05-16 12:36:22] [INFO] New user registration: user123@example.com</div>
                <div className="opacity-50">[2023-05-16 12:40:15] [INFO] Payment received: tx_1234567</div>
                <div className="text-red-300">[2023-05-16 12:45:30] [ERROR] Failed to send email: rate limit exceeded</div>
                <div className="opacity-50">[2023-05-16 12:46:01] [INFO] Retry email send: success</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Auth System Launch Check Component
const AuthSystemLaunchCheck = () => {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [overallStatus, setOverallStatus] = useState<string | null>(null);

  const runAuthCheck = async () => {
    setLoading(true);
    try {
      const checkResults = await checkAuthConfig();
      setResults(checkResults);
      
      // Determine overall status
      if (checkResults.some(r => r.status === 'error')) {
        setOverallStatus('error');
      } else if (checkResults.some(r => r.status === 'warning')) {
        setOverallStatus('warning');
      } else if (checkResults.length > 0) {
        setOverallStatus('success');
      }
    } catch (error) {
      console.error('Error running auth check:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      default:
        return '⏳';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Auth System Check</span>
          {overallStatus && (
            <span className={`text-sm px-2 py-1 rounded ${
              overallStatus === 'success' ? 'bg-green-100 text-green-800' :
              overallStatus === 'warning' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {overallStatus === 'success' ? 'Bereit' :
              overallStatus === 'warning' ? 'Mit Warnungen' :
              'Nicht bereit'}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          Überprüft die Konfiguration des Authentifizierungssystems
        </CardDescription>
      </CardHeader>
      <CardContent>
        {results.length > 0 ? (
          <div className="space-y-3">
            {results.map((result, index) => (
              <div key={index} className="flex p-3 border rounded">
                <div className="mr-3">{getStatusIcon(result.status)}</div>
                <div>
                  <div className="font-medium">{result.name}</div>
                  <div className="text-sm text-gray-600">{result.message}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-500 mb-4">Klicke auf den Button, um die Auth-Konfiguration zu prüfen</p>
            <button 
              onClick={runAuthCheck} 
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Überprüfe...' : 'Auth System prüfen'}
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminSupportTab;
